import { type GetInsightsPortalMultiGroupDataQuery } from 'domains/insights-portal/api'
import { getInsightsValue } from 'domains/insights-portal/utils/helpers'
import { type TrendData } from 'domains/insights-portal/utils/trendGraphs.utils'
import { type FormatterContext } from 'domains/insights-portal/utils/trendGraphs.types'
import { type MetricInfo } from './KpiOverviewTrend'

export const transformMultiGroupDataToPrimaryTrendData = (
  multiGroupData: GetInsightsPortalMultiGroupDataQuery,
  selectedMetric: string,
  selectedGroupBy: string
) => {
  const dataPoints =
    multiGroupData?.insightsPortalServiceGetInsightsPortalMultiGroupData?.dataRows || []
  const dataPointsWithData = dataPoints.filter(dataPoint =>
    dataPoint.dataItems?.some(dataItem => dataItem.groupByName === selectedGroupBy)
  )
  return dataPointsWithData.map(dataPoint => {
    const dataItems = dataPoint?.dataItems
    const primaryDate = getInsightsValue(
      dataItems.find(item => item.groupByName === selectedGroupBy)?.value
    )
    const comparisonDate = getInsightsValue(
      dataItems.find(item => item.groupByName === `${selectedGroupBy}_HISTORICAL`)?.value
    )

    const metricValue = Number(
      getInsightsValue(dataItems.find(item => item.metricName === selectedMetric)?.value)
    )
    const absChange = getInsightsValue(
      dataItems.find(item => item.metricName === `${selectedMetric}_ABS_DIFF`)?.value
    )
    const percentChange = getInsightsValue(
      dataItems.find(item => item.metricName === `${selectedMetric}_PCT_DIFF`)?.value
    )

    return {
      x: new Date(primaryDate).valueOf(),
      y: metricValue,
      primaryDate: primaryDate,
      primarySecondaryMetricValues: null,
      growth: absChange,
      growthPercentage: percentChange,
      comparisonDate: comparisonDate,
    }
  })
}

export const extractMetrics = (metricInfo: MetricInfo[], selectedMetric: string) => {
  const listOfMetricsWithoutSpecificSchemas = metricInfo
    .filter(metricObject => metricObject.metricSpecificSchema === undefined)
    .map(({ apiMetric, additionalMetricsToSendInRequest }) => [
      apiMetric,
      ...(additionalMetricsToSendInRequest ? additionalMetricsToSendInRequest : []).flat(),
    ])
    .flat()

  const listOfBenchmarkMetrics = metricInfo
    .map(({ benchmarkMetric }) => benchmarkMetric)
    .filter(element => element !== undefined)

  const listOfHistoricalMetrics = metricInfo
    .map(({ historicalMetric }) => historicalMetric)
    .filter(element => element !== undefined)

  const listOfAbsDiffMetrics = metricInfo.map(({ apiMetric }) => `${apiMetric}_ABS_DIFF`)
  const listOfPctDiffMetrics = metricInfo.map(({ apiMetric }) => `${apiMetric}_PCT_DIFF`)

  listOfHistoricalMetrics.push(...[...listOfAbsDiffMetrics, ...listOfPctDiffMetrics])

  const selectedMetricHistorical = metricInfo.find(
    metric => metric.apiMetric === selectedMetric
  )?.historicalMetric
  const selectedMetricFormatter = metricInfo.find(
    metric => metric.apiMetric === selectedMetric
  )?.formatter

  return {
    listOfMetricsWithoutSpecificSchemas,
    listOfBenchmarkMetrics,
    listOfHistoricalMetrics,
    selectedMetricHistorical,
    selectedMetricFormatter,
  }
}

export const mergeDataGroupedBySpecificMetricSchema = dataGroupedBySpecificMetricSchema => {
  const result = {}
  Object.keys(dataGroupedBySpecificMetricSchema).forEach(schema => {
    Object.assign(result, dataGroupedBySpecificMetricSchema[schema])
  })
  return result
}

export const groupMetricsBySpecificSchema = (metricInfo: MetricInfo[]) =>
  metricInfo
    .filter(metricObject => metricObject.metricSpecificSchema !== undefined)
    .reduce((result, item) => {
      ;(result[item.metricSpecificSchema] = result[item.metricSpecificSchema] || []).push(item)
      return result
    }, {})

export const transformMultiGroupDataToComparisonTrendData = (
  multiGroupData: GetInsightsPortalMultiGroupDataQuery,
  comparisonMetric: string,
  selectedGroupBy: string
) => {
  const dataPoints =
    multiGroupData?.insightsPortalServiceGetInsightsPortalMultiGroupData?.dataRows || []
  const dataPointsWithData = dataPoints.filter(dataPoint =>
    dataPoint.dataItems?.some(dataItem => dataItem.groupByName === selectedGroupBy)
  )
  return dataPointsWithData.map(dataPoint => {
    const dataItems = dataPoint?.dataItems
    const primaryDate = getInsightsValue(
      dataItems.find(item => item.groupByName === selectedGroupBy)?.value
    )
    const comparisonDate = getInsightsValue(
      dataItems.find(item => item.groupByName === `${selectedGroupBy}_HISTORICAL`)?.value
    )

    const comparisonMetricValue = Number(
      getInsightsValue(dataItems.find(item => item.metricName === comparisonMetric)?.value)
    )
    return {
      x: new Date(primaryDate).valueOf(),
      y: comparisonMetricValue,
      primaryDate: primaryDate,
      comparisonSecondaryMetricValues: null,
      primarySecondaryMetricValues: null,
      comparisonDate: comparisonDate,
    }
  })
}

const hasPrimaryDataPoint = (
  primaryDataRef: TrendData['primaryData'],
  points: FormatterContext[]
) => !isNaN(primaryDataRef?.find(dataPoint => dataPoint?.x === points[0]?.x)?.y)

const hasComparisonDataPoint = (
  comparisonDataRef: TrendData['comparisonData'],
  points: FormatterContext[]
) => !isNaN(comparisonDataRef?.find(dataPoint => dataPoint?.x === points[0]?.x)?.y)

const hasBenchmarkDataPoint = (
  benchmarkDataRef: ReturnType<typeof transformMultiGroupDataToBenchmarkTrendData>,
  points: FormatterContext[]
) => !isNaN(benchmarkDataRef?.find(dataPoint => dataPoint?.x === points[0]?.x)?.y)

const getPrimaryPointFromPoints = (
  primaryDataRef: TrendData['primaryData'],
  points: FormatterContext[]
) => (hasPrimaryDataPoint(primaryDataRef, points) ? points[0] : null)

const getComparisonPointFromPoints = (
  comparisonDataRef: TrendData['comparisonData'],
  primaryDataRef: TrendData['primaryData'],
  points: FormatterContext[]
) =>
  hasComparisonDataPoint(comparisonDataRef, points)
    ? hasPrimaryDataPoint(primaryDataRef, points)
      ? points[1]
      : points[0]
    : null

const getBenchmarkPointFromPoints = (
  benchmarkDataRef: ReturnType<typeof transformMultiGroupDataToBenchmarkTrendData>,
  comparisonDataRef: TrendData['comparisonData'],
  primaryDataRef: TrendData['primaryData'],
  points: FormatterContext[]
) =>
  hasBenchmarkDataPoint(benchmarkDataRef, points)
    ? hasComparisonDataPoint(comparisonDataRef, points)
      ? hasPrimaryDataPoint(primaryDataRef, points)
        ? points[2]
        : points[1]
      : hasPrimaryDataPoint(primaryDataRef, points)
      ? points[1]
      : points[0]
    : null

export const getSeriesPointsFromDataRefs = (
  primaryDataRef: TrendData['primaryData'],
  comparisonDataRef: TrendData['comparisonData'],
  benchmarkDataRef: ReturnType<typeof transformMultiGroupDataToBenchmarkTrendData>,
  points: FormatterContext[]
) => {
  const primaryPoint = getPrimaryPointFromPoints(primaryDataRef, points)
  const comparisonPoint = getComparisonPointFromPoints(comparisonDataRef, primaryDataRef, points)
  const benchmarkPoint = getBenchmarkPointFromPoints(
    benchmarkDataRef,
    comparisonDataRef,
    primaryDataRef,
    points
  )
  return {
    primaryPoint,
    comparisonPoint,
    benchmarkPoint,
  }
}

export const transformMultiGroupDataToBenchmarkTrendData = (
  multiGroupData: GetInsightsPortalMultiGroupDataQuery,
  selectedMetric: string,
  selectedGroupBy: string
) => {
  const dataPoints =
    multiGroupData?.insightsPortalServiceGetInsightsPortalMultiGroupData?.dataRows || []
  return dataPoints.map(dataPoint => {
    const dataItems = dataPoint?.dataItems
    const primaryDate = getInsightsValue(
      dataItems?.find(item => item.groupByName === selectedGroupBy)?.value
    )
    const comparisonDate = getInsightsValue(
      dataItems?.find(item => item.groupByName === `${selectedGroupBy}_HISTORICAL`)?.value
    )
    const benchmarkMetricValue = getInsightsValue(
      dataItems?.find(item => item.metricName === `BENCHMARK_${selectedMetric}`)?.value
    )
    return {
      x: new Date(primaryDate).valueOf(),
      y: benchmarkMetricValue,
      primaryDate: primaryDate,
      comparisonSecondaryMetricValues: null,
      primarySecondaryMetricValues: null,
      comparisonDate: comparisonDate,
    }
  })
}
